import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
  getLocalizedPath: (path: string) => string;
  getAlternatePath: () => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

// Inner component that has access to router context
const LanguageProviderInner: React.FC<LanguageProviderProps> = ({ children }) => {
  const location = useLocation();
  const [language, setLanguageState] = useState<Language>('en');
  const isRTL = language === 'ar';

  // Sync language state with URL path
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLanguageState('ar');
    } else {
      setLanguageState('en');
    }
  }, [location.pathname]);

  // Load Noto Sans Arabic only when needed (keeps it out of the LCP-critical path for English users)
  useEffect(() => {
    if (language !== 'ar') return;
    const id = 'noto-arabic-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;600;700&display=swap';
    document.head.appendChild(link);
  }, [language]);

  // Helper to get the localized path based on current language
  const getLocalizedPath = (path: string): string => {
    // Remove /ar prefix if present
    const cleanPath = path.replace(/^\/ar/, '') || '/';
    
    if (language === 'ar') {
      return `/ar${cleanPath === '/' ? '' : cleanPath}`;
    }
    return cleanPath;
  };

  // Get the alternate language path for the current page
  const getAlternatePath = (): string => {
    const currentPath = location.pathname;
    
    if (currentPath.startsWith('/ar')) {
      // Currently on Arabic, return English path
      const englishPath = currentPath.replace(/^\/ar/, '') || '/';
      return englishPath;
    } else {
      // Currently on English, return Arabic path
      return `/ar${currentPath === '/' ? '' : currentPath}`;
    }
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isRTL, getLocalizedPath, getAlternatePath }}>
      <div dir={isRTL ? 'rtl' : 'ltr'} className={isRTL ? 'font-arabic' : 'font-inter'}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

// Wrapper for when we're not yet inside BrowserRouter
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  return <LanguageProviderInner>{children}</LanguageProviderInner>;
};
